import {
  createCancelResponseEvent,
  createToolResponseEvent,
  createTriggerInferenceEvent,
  getResponseCreateClientEventId,
  RealtimeErrorEvent,
  ResponseCreatedEvent,
  ResponseDoneEvent,
} from 'llm/realtime-voice/OpenAIRealtimeEvents';

import WebRTCSession, {DATA_EVENT_WAIT_TIMEOUT_MS} from './WebRTCSession';

// A tagged union keeps each phase's required IDs and flags together for TypeScript narrowing.
type ResponseLifecycle =
  | {phase: 'idle' | 'closed'}
  | {phase: 'requested'; eventId: string; interrupted: boolean}
  | {phase: 'active'; responseId: string; interrupted: boolean}
  | {phase: 'cancelling'; responseId: string; eventId: string; sent: boolean};

type ToolResult = {status: 'running' | 'sent'} | {status: 'queued'; output: string};

interface CoordinatorOptions {
  onFailure: (error: unknown, phase: string) => void;
}

/**
 * OpenAI allows only one active response per conversation. A user turn or tool result can arrive
 * while another response is running; requesting a reply then is rejected, and requesting one
 * while the user speaks can miss their instructions.
 *
 * This coordinator sends each tool result once and starts the next response after the user turn
 * is committed and the current response ends. Server VAD still detects speech and interrupts;
 * automatic response creation is disabled. Pending work survives interruptions within this
 * session and is discarded when the session closes.
 */
export default class RealtimeVoiceResponseCoordinator {
  private response: ResponseLifecycle = {phase: 'idle'};
  private responseNeeded = false;
  // A new user turn may need an answer even while an earlier tool is still working.
  private userTurnPending = false;
  private transportReady = false;
  private readonly pendingUserItemIds = new Set<string>();
  private lastCommittedUserItemId: string | null = null;
  // Retain sent call IDs until session end to reject duplicate execution and output delivery.
  private readonly tools = new Map<string, ToolResult>();
  // A clear rejection can arrive after response.done or another response's cancellation.
  private readonly outputAudioClearEventIds = new Set<string>();
  private requestSequence = 0;
  private acknowledgementTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly transport: Pick<WebRTCSession, 'sendDataEvent'>,
    private readonly options: CoordinatorOptions
  ) {}

  /** Permanently close this coordinator, including callbacks that arrive after session teardown. */
  clear(): void {
    this.response = {phase: 'closed'};
    this.transportReady = false;
    this.responseNeeded = false;
    this.userTurnPending = false;
    this.pendingUserItemIds.clear();
    this.tools.clear();
    this.outputAudioClearEventIds.clear();
    this.clearTimeout();
  }

  setTransportReady(ready: boolean): void {
    this.transportReady = ready;
    this.flush();
  }

  handleUserSpeechStarted(itemId: string): void {
    if (this.response.phase === 'closed') {
      return;
    }
    this.pendingUserItemIds.add(itemId);
    if (this.response.phase === 'requested' || this.response.phase === 'active') {
      this.response.interrupted = true;
    }
  }

  handleInputAudioCleared(): void {
    this.pendingUserItemIds.clear();
    this.flush();
  }

  handleUserTurnCommitted(itemId: string): void {
    if (this.response.phase === 'closed' || itemId === this.lastCommittedUserItemId) {
      return;
    }
    // The ordered data channel delivers each committed item once; the hook can see both item aliases.
    this.lastCommittedUserItemId = itemId;
    // Speech stopping is not enough: the user item must be in the conversation before we respond.
    this.pendingUserItemIds.delete(itemId);
    this.userTurnPending = true;
    this.responseNeeded = true;
    this.flush();
  }

  handleToolCallStarted(callId: string): boolean {
    if (this.response.phase === 'closed' || this.tools.has(callId)) {
      return false;
    }
    this.tools.set(callId, {status: 'running'});
    return true;
  }

  handleToolCallSettled(callId: string): void {
    // A tool without an output cannot be retried automatically either.
    if (this.tools.get(callId)?.status === 'running') {
      this.tools.set(callId, {status: 'sent'});
    }
    this.flush();
  }

  enqueueToolOutput(callId: string, output: string): void {
    if (this.response.phase === 'closed' || this.tools.get(callId)?.status !== 'running') {
      return;
    }
    this.tools.set(callId, {status: 'queued', output});
    this.responseNeeded = true;
    this.flush();
  }

  handleResponseCreated(event: ResponseCreatedEvent): void {
    if (
      this.response.phase !== 'requested' ||
      getResponseCreateClientEventId(event) !== this.response.eventId
    ) {
      return;
    }
    const {interrupted} = this.response;
    this.clearTimeout();
    // Speech may have started before the server acknowledged our request. Now that we have
    // the response ID, explicitly cancel it instead of letting that late response play.
    this.response = interrupted
      ? {
          phase: 'cancelling',
          responseId: event.response.id,
          eventId: `voice_response_cancel_${event.response.id}`,
          sent: false,
        }
      : {phase: 'active', responseId: event.response.id, interrupted: false};
    this.flush();
  }

  handleResponseDone(event: ResponseDoneEvent): void {
    const response = this.response;
    if (
      (response.phase !== 'active' && response.phase !== 'cancelling') ||
      event.response.id !== response.responseId
    ) {
      return;
    }
    this.clearTimeout();
    switch (event.response.status) {
      case 'completed':
        break;
      case 'cancelled':
        // Keep the continuation through every barge-in, even when no new tool result arrives.
        this.responseNeeded ||= response.phase === 'cancelling' || response.interrupted;
        break;
      case 'failed':
      case 'incomplete':
        this.fail(new Error(`Voice response ${event.response.status}.`), `response-${event.response.status}`);
        return;
      default:
        this.fail(new Error('Unrecognized Voice response status.'), 'response-invalid');
        return;
    }
    this.response = {phase: 'idle'};
    this.flush();
  }

  handleRealtimeError(event: RealtimeErrorEvent): void {
    if (event.error.event_id && this.outputAudioClearEventIds.has(event.error.event_id)) {
      this.fail(new Error(event.error.message), 'output-audio-clear-rejected');
      return;
    }
    const response = this.response;
    if (
      (response.phase === 'requested' || response.phase === 'cancelling') &&
      event.error.event_id === response.eventId
    ) {
      this.fail(
        new Error(event.error.message),
        response.phase === 'requested' ? 'response-create-rejected' : 'response-cancel-rejected'
      );
    }
  }

  /** Deliver queued results, then request a response only when the conversation can accept it. */
  private flush(): void {
    if (this.response.phase === 'closed' || !this.transportReady) {
      return;
    }
    if (this.response.phase === 'cancelling' && !this.response.sent) {
      this.response.sent = true;
      this.sendCancellation(this.response.responseId, this.response.eventId);
      return;
    }
    this.sendQueuedToolResults();
    if (this.canRequestResponse()) {
      this.requestResponse();
    }
  }

  private sendCancellation(responseId: string, eventId: string): void {
    // This handles a response acknowledged after the user interrupted.
    // Once we have its ID, we can cancel it explicitly.
    this.waitForAcknowledgement('response-cancel-timeout');
    this.send(createCancelResponseEvent(responseId, eventId), 'response-cancel-send');
    // Clear buffered assistant audio. Cancelling generation alone does not clear audio already buffered
    // by WebRTC. Clear it here before starting a new response.
    const clearEventId = `voice_output_audio_clear_${responseId}`;
    this.outputAudioClearEventIds.add(clearEventId);
    this.send({type: 'output_audio_buffer.clear', event_id: clearEventId}, 'response-cancel-send');
  }

  private sendQueuedToolResults(): void {
    // Adding a tool result to the conversation does not itself start a response. Deliver it
    // even during speech or generation; keep responseNeeded until a later response can use it.
    for (const [callId, result] of this.tools) {
      if (result.status === 'queued') {
        this.tools.set(callId, {status: 'sent'});
        this.send(createToolResponseEvent(callId, result.output), 'tool-output-send');
      }
    }
  }

  private canRequestResponse(): boolean {
    // Wait if:
    // - A response is requested, active, or being cancelled.
    // - Nothing needs a follow-up.
    // - A detected user turn hasn't yet been committed to the conversation, even if speech has stopped.
    if (this.response.phase !== 'idle' || !this.responseNeeded || this.pendingUserItemIds.size > 0) {
      return false;
    }
    // Let the user ask a question while tools run, e.g. "What are you doing?";
    // otherwise wait for their results.
    const hasRunningTools = [...this.tools.values()].some((tool) => tool.status === 'running');
    return this.userTurnPending || !hasRunningTools;
  }

  private requestResponse(): void {
    const eventId = `voice_response_create_${++this.requestSequence}`;
    // Reserve the response slot, then request generation.
    this.response = {phase: 'requested', eventId, interrupted: false};
    this.responseNeeded = false;
    this.userTurnPending = false;
    this.waitForAcknowledgement('response-create-timeout');
    this.send(createTriggerInferenceEvent(eventId), 'response-create-send');
  }

  private send(event: unknown, phase: string): void {
    try {
      this.transport.sendDataEvent(event);
    } catch (error) {
      this.fail(error, phase);
      throw error;
    }
  }

  private waitForAcknowledgement(phase: string): void {
    this.clearTimeout();
    // A wall-clock bound also covers a disconnected transport; uncertain sends are never retried.
    this.acknowledgementTimeout = setTimeout(() => {
      this.fail(new Error('Timed out waiting for the Voice response acknowledgement.'), phase);
    }, DATA_EVENT_WAIT_TIMEOUT_MS);
  }

  private clearTimeout(): void {
    if (this.acknowledgementTimeout != null) {
      clearTimeout(this.acknowledgementTimeout);
      this.acknowledgementTimeout = null;
    }
  }

  private fail(error: unknown, phase: string): void {
    this.clear();
    this.options.onFailure(error, phase);
  }
}
